import {
  Feature,
  getFeatureByAddOn,
  getFeatureFromPlan,
  isSamePlanIgnoringEcom,
  isTrueBundleFeature,
  toRecurlyAddOnCode,
  LineItem,
  OrderPreviewRequest,
  OrderPreviewResponse,
  OrderProductPreview,
  PlanInterval,
  ProductPreviewRequest,
  RecurlyAddOn,
  Subscription,
  SubscriptionSource,
  SubscriptionPlanPrice,
} from '../../../../models';
import { getUnitPrice } from '../../../../hook';
import {
  DEFAULT_TRUE_BUNDLE_FEATURE,
  getTrueBundlePlan,
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PURCHASE_FEATURES,
  TRUE_BUNDLE_STARTER_FEATURES,
} from './trueBundlePlans';

const PAID_RECURLY_ADD_ONS = Object.values(RecurlyAddOn).filter(
  addOn => addOn !== RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE,
);

export const getTrueBundlePreviewRequests = (
  orderPreviewRequest?: OrderPreviewRequest,
  fallbackPreviewRequest?: ProductPreviewRequest,
): ProductPreviewRequest[] => {
  if (orderPreviewRequest?.singleProductList?.length) {
    return orderPreviewRequest.singleProductList;
  }

  return fallbackPreviewRequest ? [fallbackPreviewRequest] : [];
};

const getPreviewRequestFeature = (
  request: ProductPreviewRequest,
): Feature => getFeatureFromPlan(request.productCode) ?? DEFAULT_TRUE_BUNDLE_FEATURE;

export const getPreviewProductFeature = (
  product: OrderProductPreview,
): Feature => getFeatureFromPlan(product.productCode) ?? product.feature;

export const getPreviewExistingQuantity = (
  product: Pick<OrderProductPreview, 'quantity' | 'newQuantity'>,
): number => Math.max(product.quantity - product.newQuantity, 0);

export const isMonthlyToAnnualSwitchPreview = (
  product: OrderProductPreview,
  currentSubscriptions: Subscription[] = [],
): boolean => {
  const feature = getPreviewProductFeature(product);

  return product.billingInterval === PlanInterval.YEAR
    && product.creditAmount > 0
    && currentSubscriptions.some(subscription => subscription.feature === feature
      && subscription.source === SubscriptionSource.RECURLY
      && subscription.billingInterval === PlanInterval.MONTH);
};

export const isMonthlyToAnnualSwitchRequest = (
  product: ProductPreviewRequest,
  currentSubscriptions: Subscription[] = [],
): boolean => {
  const feature = getPreviewRequestFeature(product);

  return product.quantity === 0
    && !!product.recurlySubscriptionUuid
    && PlanInterval.getIntervalFromPlanCode(product.productCode) === PlanInterval.YEAR
    && currentSubscriptions.some(subscription => subscription.feature === feature
      && subscription.source === SubscriptionSource.RECURLY
      && subscription.billingInterval === PlanInterval.MONTH
      && subscription.recurlySubUuId === product.recurlySubscriptionUuid);
};

export const hasSelectedPreviewRequest = (
  request: ProductPreviewRequest,
  currentSubscriptions: Subscription[] = [],
): boolean => request.quantity > 0
  || Object.values(request.addOns ?? {}).some(quantity => quantity > 0)
  || isMonthlyToAnnualSwitchRequest(request, currentSubscriptions);

/**
 * Whether the cart holds a True Bundle Starter. One order carries ONE campaign (the BE answers
 * `promotion.multiple.campaigns.in.order` otherwise), so while a bundle is in the cart every other
 * row must be presented without its own campaign (no KS Starter band / tag / promo price).
 */
export const hasTrueBundleInPreviewRequests = (
  requests: ProductPreviewRequest[],
): boolean => requests.some(request => request.quantity > 0
  && isTrueBundleFeature(getFeatureFromPlan(request.productCode)));

/**
 * The campaign a row may be priced with: the bundle's own campaign for a bundle product, nothing
 * for any other product while a bundle is in the cart, and the product's campaign otherwise.
 */
export const gateCampaignForTrueBundleCart = <T>(
  campaign: T | null,
  productCode: string,
  isTrueBundleInCart: boolean,
): T | null => {
  const isOtherProductOfBundleCart = isTrueBundleInCart
    && !isTrueBundleFeature(getFeatureFromPlan(productCode));
  return isOtherProductOfBundleCart ? null : campaign;
};

/**
 * SKUs that cannot join an order holding a Starter bundle. Katalon Studio is the only other SKU
 * with a live campaign (KS Starter), and 1 order = 1 campaign - so rather than selling it at the
 * regular price next to the bundle, the page keeps it out of that order entirely (it is bought in
 * a separate order). Every other SKU (TestOps, KRE, TestCloud) still rides along with the bundle.
 */
const TRUE_BUNDLE_NON_COMBINABLE_FEATURES: ReadonlySet<Feature> = new Set<Feature>([
  Feature.PER_USER_KSE,
]);

export const isBlockedByTrueBundleCart = (
  feature: Feature,
  isTrueBundleInCart: boolean,
): boolean => isTrueBundleInCart && TRUE_BUNDLE_NON_COMBINABLE_FEATURES.has(feature);

export const getTrueBundleTitleId = (
  feature: Feature,
) => {
  if (TRUE_BUNDLE_STARTER_FEATURES.includes(feature)) {
    return getTrueBundlePlan(feature).titleId;
  }

  if (TRUE_BUNDLE_PURCHASE_FEATURES.includes(feature)) {
    return getTrueBundlePlan(feature).titleId;
  }

  if (TRUE_MORE_TESTING_PRODUCT_FEATURES.includes(feature)) {
    return `feature.${feature.toLowerCase()}.product.name`;
  }

  return 'page.plan.v2.plan.title';
};

const buildLineItem = (
  planId: string,
  addOnCode: string,
  quantity: number,
  unitAmount: number,
): LineItem => ({
  addOnCode,
  displayName: addOnCode,
  discountAmount: 0,
  planId,
  prorationRate: 1,
  quantity,
  subtotal: quantity * unitAmount,
  tax: 0,
  taxRate: 0,
  unitAmount,
});

export const buildFallbackPreviewProduct = (
  request: ProductPreviewRequest,
  planPrice: SubscriptionPlanPrice | undefined,
): OrderProductPreview => {
  const feature = getPreviewRequestFeature(request);
  const billingInterval = PlanInterval.getIntervalFromPlanCode(request.productCode);
  const unitAmount = getUnitPrice(planPrice, feature, billingInterval, 'product');
  const baseLineItem = buildLineItem(
    request.productCode,
    request.productCode,
    request.quantity,
    unitAmount,
  );
  const addOnLineItems = Object.entries(request.addOns ?? {})
    .filter(([, quantity]) => quantity > 0)
    .map(([addOnKey, quantity]) => {
      const addOnCode = toRecurlyAddOnCode(addOnKey);
      const addOnFeature = getFeatureByAddOn(addOnCode);
      const addOnUnitAmount = getUnitPrice(planPrice, addOnFeature, billingInterval, 'addOn');
      return buildLineItem(
        request.productCode,
        addOnCode,
        quantity,
        addOnUnitAmount,
      );
    });
  const addOnSubTotal = addOnLineItems
    .reduce((total, lineItem) => total + lineItem.subtotal, 0);

  return {
    accountId: 0,
    billingInterval,
    creditAmount: 0,
    discountAmount: 0,
    discountQuantity: 0,
    feature,
    lineItem: [
      baseLineItem,
      ...addOnLineItems,
    ],
    newQuantity: request.quantity,
    productCode: request.productCode,
    prorationRate: 1,
    quantity: request.quantity,
    subTotal: baseLineItem.subtotal + addOnSubTotal,
    tax: 0,
    taxRate: 0,
    total: baseLineItem.subtotal + addOnSubTotal,
    unitAmount,
  };
};

export const getMatchedPreviewProduct = (
  request: ProductPreviewRequest,
  response: OrderPreviewResponse | undefined,
): OrderProductPreview | undefined => {
  // Requests key add-ons by the BE enum NAME, responses by the add-on CODE - join on the code.
  const requestedAddOns = Object.fromEntries(
    Object.entries(request.addOns ?? {})
      .filter(([, quantity]) => quantity > 0)
      .map(([addOnKey, quantity]) => [toRecurlyAddOnCode(addOnKey), quantity]),
  );
  const requestedAddOnCount = Object.keys(requestedAddOns).length;

  return response?.productsPreview.find(product => {
    const paidAddOns = product.lineItem.filter(
      item => PAID_RECURLY_ADD_ONS.includes(item.addOnCode as RecurlyAddOn),
    );

    // BE may route an eligible account's legacy KSE code to its `_ecom_` sibling (product#1447),
    // so the response code is joined back to the request's legacy code ecom-insensitively.
    return isSamePlanIgnoringEcom(product.productCode, request.productCode)
      && product.newQuantity === request.quantity
      && (
        request.recurlySubscriptionUuid
        || product.quantity === request.quantity
      )
      && paidAddOns.length === requestedAddOnCount
      && paidAddOns.every(item => requestedAddOns[item.addOnCode] === item.quantity);
  });
};

export const hasPreviewResponseForRequests = (
  requests: ProductPreviewRequest[],
  response: OrderPreviewResponse | undefined,
): boolean => {
  if (requests.length === 0) {
    return true;
  }

  if (!response || response.productsPreview.length !== requests.length) {
    return false;
  }

  return requests.every(request => !!getMatchedPreviewProduct(request, response));
};

export const buildPreviewProducts = (
  requests: ProductPreviewRequest[],
  response: OrderPreviewResponse | undefined,
  planPrice: SubscriptionPlanPrice | undefined,
): OrderProductPreview[] => requests.map(request => {
  const matchedProduct = getMatchedPreviewProduct(request, response);
  return matchedProduct ?? buildFallbackPreviewProduct(request, planPrice);
});

const isPaidAddOnLineItem = (lineItem: LineItem): boolean => (
  (Object.values(RecurlyAddOn) as string[]).includes(lineItem.addOnCode)
  && lineItem.addOnCode !== RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE
);

/** A True Bundle preview product: one package, priced only on its parent row (Decision 2). */
export const isTrueBundlePreviewProduct = (
  product: OrderProductPreview,
): boolean => isTrueBundleFeature(getPreviewProductFeature(product));

/**
 * The components of a bundle preview, in the order Recurly priced them. They are rendered as
 * name + quantity WITHOUT any amount: the whole package price sits on the parent row, and a
 * fixed-amount campaign is allocated greedily across the component lines, so a per-line price
 * would show one component discounted to nothing next to others at full price.
 */
export const getBundleComponentLineItems = (
  product: OrderProductPreview,
): LineItem[] => (isTrueBundlePreviewProduct(product)
  ? product.lineItem.filter(isPaidAddOnLineItem)
  : []);

/**
 * The FREE TestCloud Cross-Browser line the BE adds to a bundle preview (quantity from LD
 * `truePlatformConfig`, the same maths as provisioning). It is deliberately NOT one of
 * [getBundleComponentLineItems] - it is not a paid component and is drawn last on its own - and
 * it is absent from a preview answered by a BE that predates it: the caller then falls back to
 * [getTrueBundleFreeCrossBrowserSessions].
 */
export const getBundleFreeCrossBrowserLineItem = (
  product: OrderProductPreview,
): LineItem | undefined => (isTrueBundlePreviewProduct(product)
  ? product.lineItem.find(
    lineItem => lineItem.addOnCode === RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE,
  )
  : undefined);

/**
 * Add-on lines that are billed as their OWN summary row (the legacy True Team TestCloud add-ons).
 * Bundle components are excluded: they belong under their parent row and carry no price.
 */
export const getPaidAddOnLineItems = (
  products: OrderProductPreview[],
): LineItem[] => products
  .filter(product => !isTrueBundlePreviewProduct(product))
  .flatMap(product => product.lineItem.filter(isPaidAddOnLineItem));

// HARDCODED FLAT step. The BE does NOT use a constant: it reads the step from the LD flag
// `truePlatformConfig` (FLAT `stepConfig` or STAIR_STEP). If ops change that flag, change this
// constant with it - otherwise the free TC-CB line on the bundle card / Order Summary no longer
// matches the number the BE actually grants.
const FREE_CROSS_BROWSER_SESSION_SEAT_STEP = 5;

export const getSelectedTrueBundleSeatQuota = (
  products: OrderProductPreview[],
): number => products
  .filter(product => TRUE_BUNDLE_PURCHASE_FEATURES.includes(
    getPreviewProductFeature(product),
  ))
  .reduce((sum, product) => sum + Math.max(product.newQuantity, 0), 0);

const getCurrentPaidTrueBundleSeatQuota = (
  currentSubscriptions: Subscription[] = [],
): number => currentSubscriptions
  .filter(subscription => subscription.source === SubscriptionSource.RECURLY
    && TRUE_BUNDLE_PURCHASE_FEATURES.includes(subscription.feature))
  .reduce((sum, subscription) => sum + Math.max(subscription.quota, 0), 0);

const getPreviewExistingTrueBundleSeatQuota = (
  products: OrderProductPreview[],
): number => products
  .filter(product => TRUE_BUNDLE_PURCHASE_FEATURES.includes(
    getPreviewProductFeature(product),
  ))
  .reduce((sum, product) => sum + getPreviewExistingQuantity(product), 0);

export const getFinalTrueBundleSeatQuota = (
  products: OrderProductPreview[],
  currentSubscriptions: Subscription[] = [],
): number => {
  const currentTrueSeats = currentSubscriptions.length > 0
    ? getCurrentPaidTrueBundleSeatQuota(currentSubscriptions)
    : getPreviewExistingTrueBundleSeatQuota(products);

  return currentTrueSeats + getSelectedTrueBundleSeatQuota(products);
};

/**
 * Free TestCloud Cross-Browser sessions granted for a number of True seats: one per started block
 * of 5 (mirrors the BE `calculateFreeTestCloudQuota` ONLY while LD `truePlatformConfig` is FLAT
 * step 5 - see `FREE_CROSS_BROWSER_SESSION_SEAT_STEP`). The BE is the source of truth; the FE only
 * uses this to PREVIEW the free sessions it will grant.
 */
export const getFreeCrossBrowserSessionQuota = (
  trueSeatQuota: number,
): number => (trueSeatQuota > 0
  ? Math.ceil(trueSeatQuota / FREE_CROSS_BROWSER_SESSION_SEAT_STEP)
  : 0);

export const getCloudSessionQuota = (
  products: OrderProductPreview[],
  currentSubscriptions: Subscription[] = [],
): number => {
  const finalTrueSeats = getFinalTrueBundleSeatQuota(products, currentSubscriptions);

  if (finalTrueSeats <= 0) {
    return 0;
  }

  const currentTrueSeats = currentSubscriptions.length > 0
    ? getCurrentPaidTrueBundleSeatQuota(currentSubscriptions)
    : getPreviewExistingTrueBundleSeatQuota(products);

  return Math.max(
    getFreeCrossBrowserSessionQuota(finalTrueSeats)
      - getFreeCrossBrowserSessionQuota(currentTrueSeats),
    0,
  );
};
